// =====================================================================
// Text styling
// =====================================================================
// Typography shared by the editor preview and the Shotstack export.
// Shotstack's HTML renderer cannot load web fonts, so families map to
// stacks that end in a generic family every renderer has.
// =====================================================================

export type TextFont = 'sans' | 'serif' | 'mono';

export interface TextStyle {
  fontFamily: TextFont;
  /** 300–900 */
  fontWeight: number;
  italic: boolean;
  uppercase: boolean;
  /** em, -0.05–0.5 */
  letterSpacing: number;
  /** Multiple of the font size, 0.9–2 */
  lineHeight: number;
  shadow: boolean;
  /** 0.1–1 */
  opacity: number;
  /** Text box width as a share of the frame width, 0.2–0.95 */
  width: number;
}

export const DEFAULT_TEXT_STYLE: TextStyle = {
  fontFamily: 'sans',
  fontWeight: 500,
  italic: false,
  uppercase: false,
  letterSpacing: 0,
  lineHeight: 1.25,
  shadow: false,
  opacity: 1,
  width: 0.86,
};

export const TEXT_FONTS: Array<{ value: TextFont; label: string; stack: string }> = [
  { value: 'sans', label: 'Sans', stack: 'Helvetica, Arial, sans-serif' },
  { value: 'serif', label: 'Serif', stack: "Georgia, 'Times New Roman', serif" },
  { value: 'mono', label: 'Mono', stack: "'Courier New', Courier, monospace" },
];

export const TEXT_WEIGHTS: Array<{ value: number; label: string }> = [
  { value: 300, label: 'Light' },
  { value: 400, label: 'Regular' },
  { value: 500, label: 'Medium' },
  { value: 700, label: 'Bold' },
  { value: 900, label: 'Black' },
];

export const MIN_TEXT_WIDTH = 0.2;
export const MAX_TEXT_WIDTH = 0.95;

function clamp(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}

export function fontStack(font: TextFont | undefined): string {
  return (TEXT_FONTS.find((f) => f.value === font) ?? TEXT_FONTS[0]).stack;
}

/** Fill in and clamp optional style fields from a saved text item. */
export function resolveTextStyle(raw: Partial<TextStyle>): TextStyle {
  const d = DEFAULT_TEXT_STYLE;
  return {
    fontFamily: TEXT_FONTS.some((f) => f.value === raw.fontFamily) ? (raw.fontFamily as TextFont) : d.fontFamily,
    fontWeight: Math.round(clamp(Number(raw.fontWeight ?? d.fontWeight), 300, 900) / 100) * 100,
    italic: raw.italic ?? d.italic,
    uppercase: raw.uppercase ?? d.uppercase,
    letterSpacing: clamp(Number(raw.letterSpacing ?? d.letterSpacing), -0.05, 0.5),
    lineHeight: clamp(Number(raw.lineHeight ?? d.lineHeight), 0.9, 2),
    shadow: raw.shadow ?? d.shadow,
    opacity: clamp(Number(raw.opacity ?? d.opacity), 0.1, 1),
    width: clamp(Number(raw.width ?? d.width), MIN_TEXT_WIDTH, MAX_TEXT_WIDTH),
  };
}

export const TEXT_SHADOW = '0 0.06em 0.25em rgba(0,0,0,0.65)';

/** Rough wrapped line count, used to size the export's text box. */
export function estimateTextLines(text: string, fontPx: number, boxPx: number, style: TextStyle): number {
  const glyph = fontPx * (0.52 + style.letterSpacing) * (style.fontWeight >= 700 ? 1.08 : 1) * (style.uppercase ? 1.15 : 1);
  const perLine = Math.max(1, Math.floor(boxPx / Math.max(1, glyph)));
  return text
    .split('\n')
    .reduce((sum, para) => sum + Math.max(1, Math.ceil(para.length / perLine)), 0);
}

export interface TextPreset {
  id: string;
  label: string;
  text: string;
  role: 'title' | 'heading' | 'subheading' | 'body' | 'detail' | 'caption' | 'lowerThird' | 'endcard';
  fontSize: number;
  y: number;
  background: string | null;
  align?: 'left' | 'center' | 'right';
  style: Partial<TextStyle>;
}

export const TEXT_PRESETS: TextPreset[] = [
  { id: 'title', label: 'Title', text: 'Title', role: 'title', fontSize: 80, y: 0.2, background: null, style: { fontWeight: 900, shadow: true, lineHeight: 1.1 } },
  { id: 'heading', label: 'Heading', text: 'Heading', role: 'heading', fontSize: 58, y: 0.3, background: null, style: { fontWeight: 700, shadow: true, lineHeight: 1.15 } },
  { id: 'subheading', label: 'Subheading', text: 'Subheading', role: 'subheading', fontSize: 34, y: 0.38, background: null, style: { fontWeight: 500, uppercase: true, letterSpacing: 0.14, shadow: true } },
  { id: 'body', label: 'Body', text: 'Body text', role: 'body', fontSize: 34, y: 0.5, background: null, style: { fontWeight: 400, shadow: true } },
  { id: 'detail', label: 'Detail', text: 'Detail', role: 'detail', fontSize: 24, y: 0.9, background: null, style: { fontWeight: 300, letterSpacing: 0.06, shadow: true } },
  { id: 'caption', label: 'Caption', text: 'Caption', role: 'caption', fontSize: 32, y: 0.82, background: 'rgba(0,0,0,0.45)', style: { fontWeight: 500 } },
  { id: 'lowerThird', label: 'Lower third', text: 'Lower third', role: 'lowerThird', fontSize: 34, y: 0.86, background: 'rgba(15,23,42,0.85)', align: 'left', style: { fontWeight: 600, width: 0.9 } },
  { id: 'endcard', label: 'End card', text: 'End card', role: 'endcard', fontSize: 52, y: 0.5, background: null, style: { fontWeight: 700, shadow: true } },
];
